import { useCallback, useEffect, useMemo, useState } from 'react';
import { decimals, Empty, Field, Note, Panel, Spinner, TableWrap } from '@wyocspay/ui';
import { money, dateTime } from './format.js';
import { ApiError, api, type Invoice, type InvoiceInput, type InvoiceMeta } from './api.js';

/**
 * Invoicing.
 *
 * A merchant lists, writes, issues, collects and credits invoices here. The
 * builder is a document canvas (bill-to, line items, totals) beside a settings
 * rail (currency, terms, tax) - the shape people expect from an invoice tool.
 * Money is shown and entered in the invoice's own currency; the amounts on the
 * wire are always integer minor units, converted at the edge with the currency's
 * real decimal scale. Totals are previewed with the same arithmetic the server
 * uses, so the figure on screen is the figure that will be stored.
 */

function msg(e: unknown): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
}

const STATUS_PILL: Record<string, { bg: string; fg: string; label: string }> = {
  draft: { bg: '#EEF2F7', fg: '#5E7086', label: 'Draft' },
  sent: { bg: '#E6F0FB', fg: '#1E46A8', label: 'Sent' },
  partial: { bg: '#FBF3E3', fg: '#9A6A12', label: 'Part paid' },
  overdue: { bg: '#FBEDED', fg: '#B23A3E', label: 'Overdue' },
  paid: { bg: '#E7F6EF', fg: '#0E7A5A', label: 'Paid' },
  cancelled: { bg: '#EEF2F7', fg: '#93A2B4', label: 'Cancelled' },
};

function StatusPill({ status }: { status: string }) {
  const s = STATUS_PILL[status] ?? STATUS_PILL.sent;
  return (
    <span className="inv-pill" style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Currency-aware minor/major conversion
// ---------------------------------------------------------------------------

function toMinor(major: string, currency: string): number {
  const scale = 10 ** decimals(currency);
  const n = Number.parseFloat(major || '0');
  return Number.isFinite(n) ? Math.round(n * scale) : 0;
}
function toMajor(minor: number, currency: string): string {
  const d = decimals(currency);
  return (minor / 10 ** d).toFixed(d);
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

type Screen = { mode: 'list' } | { mode: 'edit'; id: string | null } | { mode: 'detail'; id: string };

export function InvoicesView() {
  const [meta, setMeta] = useState<InvoiceMeta | null>(null);
  const [screen, setScreen] = useState<Screen>({ mode: 'list' });

  useEffect(() => {
    api.invoiceMeta().then(setMeta).catch(() => undefined);
  }, []);

  if (screen.mode === 'edit') {
    return (
      <InvoiceEditor
        id={screen.id}
        meta={meta}
        onDone={(inv) => setScreen({ mode: 'detail', id: inv.id })}
        onCancel={() => setScreen({ mode: 'list' })}
      />
    );
  }
  if (screen.mode === 'detail') {
    return (
      <InvoiceDetail
        id={screen.id}
        meta={meta}
        onBack={() => setScreen({ mode: 'list' })}
        onEdit={(id) => setScreen({ mode: 'edit', id })}
      />
    );
  }
  return <InvoiceList meta={meta} onNew={() => setScreen({ mode: 'edit', id: null })} onOpen={(id) => setScreen({ mode: 'detail', id })} />;
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function InvoiceList({
  meta,
  onNew,
  onOpen,
}: {
  meta: InvoiceMeta | null;
  onNew: () => void;
  onOpen: (id: string) => void;
}) {
  const [rows, setRows] = useState<Invoice[] | null>(null);
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.invoices({ status: status || undefined, kind: kind || undefined });
      setRows(r.invoices);
    } catch (e) {
      setError(msg(e));
    }
  }, [status, kind]);

  useEffect(() => {
    void load();
  }, [load]);

  async function sweep(): Promise<void> {
    setSweeping(true);
    try {
      await api.refreshOverdueInvoices();
      await load();
    } catch (e) {
      setError(msg(e));
    } finally {
      setSweeping(false);
    }
  }

  // A small, always-correct summary from whatever is loaded. Outstanding is
  // shown in the currency it is mostly owed in, so figures are never added
  // across currencies.
  const stats = useMemo(() => {
    const live = (rows ?? []).filter((r) => r.kind === 'invoice');
    const outByCur = new Map<string, number>();
    for (const r of live) {
      if (r.status === 'cancelled' || r.status === 'paid') continue;
      outByCur.set(r.currency, (outByCur.get(r.currency) ?? 0) + r.outstanding);
    }
    let topCur: string | null = null;
    let topVal = 0;
    for (const [c, v] of outByCur) if (v >= topVal) [topCur, topVal] = [c, v];
    return {
      outstanding: topCur ? money(topVal, topCur) : '—',
      manyCurrencies: outByCur.size > 1,
      overdue: live.filter((r) => r.status === 'overdue').length,
      drafts: live.filter((r) => r.status === 'draft').length,
    };
  }, [rows]);

  return (
    <div className="inv-wrap">
      {rows && rows.length > 0 ? (
        <div className="inv-stats">
          <div className="inv-stat">
            <div className="k">Outstanding{stats.manyCurrencies ? ' (top currency)' : ''}</div>
            <div className="v">{stats.outstanding}</div>
          </div>
          <div className="inv-stat">
            <div className="k">Overdue</div>
            <div className={`v${stats.overdue > 0 ? ' due' : ''}`}>{stats.overdue}</div>
          </div>
          <div className="inv-stat">
            <div className="k">Drafts</div>
            <div className="v">{stats.drafts}</div>
          </div>
        </div>
      ) : null}

      <div className="inv-toolbar">
        <select className="wc-input" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All documents</option>
          <option value="invoice">Invoices</option>
          <option value="credit_note">Credit notes</option>
        </select>
        <select className="wc-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="partial">Part paid</option>
          <option value="overdue">Overdue</option>
          <option value="paid">Paid</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={sweep} disabled={sweeping}>
          {sweeping ? 'Refreshing…' : 'Refresh overdue'}
        </button>
        <div className="sp" />
        <button type="button" className="wc-btn" onClick={onNew}>
          New invoice
        </button>
      </div>

      {error ? <Note tone="danger">{error}</Note> : null}

      {rows === null ? (
        <Spinner label="Loading invoices…" />
      ) : rows.length === 0 ? (
        <Empty>No invoices yet. Create your first with “New invoice”.</Empty>
      ) : (
        <Panel padded={false}>
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Customer</th>
                  <th>Issued</th>
                  <th>Due</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Due</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(inv.id)}>
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--wc-ink)' }}>{inv.number}</span>
                      {inv.kind === 'credit_note' ? <span style={{ color: 'var(--wc-faint)' }}> · CN</span> : null}
                    </td>
                    <td>{inv.bill_to.name || <span style={{ color: 'var(--wc-faint)' }}>—</span>}</td>
                    <td style={{ color: 'var(--wc-muted)' }}>{inv.issued_at ? dateTime(inv.issued_at) : '—'}</td>
                    <td style={{ color: 'var(--wc-muted)' }}>{inv.due_at ? dateTime(inv.due_at) : '—'}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(inv.total, inv.currency)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: inv.outstanding > 0 ? 'var(--wc-ink)' : 'var(--wc-faint)' }}>
                      {inv.kind === 'credit_note' ? '—' : inv.outstanding > 0 ? money(inv.outstanding, inv.currency) : '—'}
                    </td>
                    <td>
                      <StatusPill status={inv.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Panel>
      )}

      {meta && !meta.pdf_available ? (
        <Note tone="warn" title="PDF export is off on this server">
          Invoices can still be created and collected online; the PDF download and email need the server’s document
          renderer, which is not configured here.
        </Note>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

interface LineDraft {
  description: string;
  quantity: string;
  unitPrice: string;
}

const TAX_PRESETS: { label: string; taxLabel: string; percent: string }[] = [
  { label: 'No tax', taxLabel: '', percent: '0' },
  { label: 'VAT 20% (UK)', taxLabel: 'VAT 20%', percent: '20' },
  { label: 'VAT 19% (DE)', taxLabel: 'VAT 19%', percent: '19' },
  { label: 'VAT 21% (EU)', taxLabel: 'VAT 21%', percent: '21' },
  { label: 'GST 5% (CA)', taxLabel: 'GST 5%', percent: '5' },
  { label: 'HST 13% (CA)', taxLabel: 'HST 13%', percent: '13' },
  { label: 'Sales tax', taxLabel: 'Sales tax', percent: '0' },
];

const DUE_TERMS: { label: string; days: number }[] = [
  { label: 'Due on receipt', days: 0 },
  { label: 'Net 7', days: 7 },
  { label: 'Net 14', days: 14 },
  { label: 'Net 30', days: 30 },
  { label: 'Net 60', days: 60 },
];

function InvoiceEditor({
  id,
  meta,
  onDone,
  onCancel,
}: {
  id: string | null;
  meta: InvoiceMeta | null;
  onDone: (inv: Invoice) => void;
  onCancel: () => void;
}) {
  const [loaded, setLoaded] = useState(id === null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [number, setNumber] = useState<string | null>(null);

  const [currency, setCurrency] = useState('usd');
  const [billName, setBillName] = useState('');
  const [billEmail, setBillEmail] = useState('');
  const [billAddress, setBillAddress] = useState('');
  const [billTaxId, setBillTaxId] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([{ description: '', quantity: '1', unitPrice: '' }]);
  const [discountPercent, setDiscountPercent] = useState('0');
  const [taxLabel, setTaxLabel] = useState('');
  const [taxPercent, setTaxPercent] = useState('0');
  const [dueDays, setDueDays] = useState('30');
  const [poRef, setPoRef] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (id === null && meta) setCurrency(meta.default_currency);
  }, [id, meta]);

  useEffect(() => {
    if (id === null) return;
    let live = true;
    api
      .invoice(id)
      .then((r) => {
        if (!live) return;
        const inv = r.invoice;
        setNumber(inv.number);
        setCurrency(inv.currency);
        setBillName(inv.bill_to.name ?? '');
        setBillEmail(inv.bill_to.email ?? '');
        setBillAddress(inv.bill_to.address ?? '');
        setBillTaxId(inv.bill_to.tax_id ?? '');
        setLines(
          inv.lines.length
            ? inv.lines.map((l) => ({ description: l.description, quantity: String(l.quantity), unitPrice: toMajor(l.unit_price, inv.currency) }))
            : [{ description: '', quantity: '1', unitPrice: '' }],
        );
        setDiscountPercent(String(inv.discount_bps / 100));
        setTaxLabel(inv.tax_label ?? '');
        setTaxPercent(String(inv.tax_bps / 100));
        setPoRef(inv.po_reference ?? '');
        setNotes(inv.notes ?? '');
        setLoaded(true);
      })
      .catch((e) => {
        if (live) setError(msg(e));
      });
    return () => {
      live = false;
    };
  }, [id]);

  const totals = useMemo(() => {
    const items = lines.map((l) => ({
      qty: Number.parseFloat(l.quantity || '0') || 0,
      unit: toMinor(l.unitPrice, currency),
    }));
    const subtotal = items.reduce((s, l) => s + Math.round(l.qty * l.unit), 0);
    const discountBps = Math.round((Number.parseFloat(discountPercent || '0') || 0) * 100);
    const taxBps = Math.round((Number.parseFloat(taxPercent || '0') || 0) * 100);
    const discountAmount = Math.round((subtotal * discountBps) / 10_000);
    const taxable = subtotal - discountAmount;
    const taxAmount = Math.round((taxable * taxBps) / 10_000);
    return { subtotal, discountAmount, taxAmount, total: taxable + taxAmount, discountBps, taxBps };
  }, [lines, discountPercent, taxPercent, currency]);

  function setLine(i: number, patch: Partial<LineDraft>): void {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine(): void {
    setLines((ls) => [...ls, { description: '', quantity: '1', unitPrice: '' }]);
  }
  function removeLine(i: number): void {
    setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
  }

  const validLines = lines.filter((l) => l.description.trim().length > 0);
  const canSave = validLines.length > 0 && !busy;

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    const body: InvoiceInput = {
      currency,
      bill_to: {
        name: billName.trim() || null,
        email: billEmail.trim() || null,
        address: billAddress.trim() || null,
        tax_id: billTaxId.trim() || null,
      },
      lines: validLines.map((l) => ({
        description: l.description.trim(),
        quantity: Number.parseFloat(l.quantity || '0') || 0,
        unit_price: toMinor(l.unitPrice, currency),
      })),
      discount_bps: totals.discountBps,
      tax_label: taxLabel.trim() || null,
      tax_bps: totals.taxBps,
      due_days: Number.parseInt(dueDays || '0', 10) || 0,
      po_reference: poRef.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      const r = id ? await api.updateInvoice(id, body) : await api.createInvoice(body);
      onDone(r.invoice);
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <Spinner label="Loading invoice…" />;

  const curLabel = currency.toUpperCase();

  return (
    <div className="inv-wrap">
      <div className="inv-head">
        <button type="button" className="inv-back" onClick={onCancel}>
          ← Back
        </button>
        <h2>{id ? `Edit ${number ?? 'invoice'}` : 'New invoice'}</h2>
        <div className="sp" />
        <button type="button" className="wc-btn wc-btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="wc-btn" onClick={save} disabled={!canSave}>
          {busy ? 'Saving…' : id ? 'Save changes' : 'Create draft'}
        </button>
      </div>

      {error ? <Note tone="danger">{error}</Note> : null}

      <div className="inv-builder">
        {/* Document canvas */}
        <div className="inv-canvas">
          <div>
            <div className="inv-eyebrow" style={{ marginBottom: 12 }}>
              Bill to
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <div className="inv-two">
                <Field label="Customer name">
                  <input className="wc-input" value={billName} onChange={(e) => setBillName(e.target.value)} placeholder="Acme Ltd" />
                </Field>
                <Field label="Customer email">
                  <input className="wc-input" type="email" value={billEmail} onChange={(e) => setBillEmail(e.target.value)} placeholder="billing@acme.com" />
                </Field>
              </div>
              <div className="inv-two">
                <Field label="Address">
                  <textarea className="wc-input" value={billAddress} onChange={(e) => setBillAddress(e.target.value)} rows={2} />
                </Field>
                <Field label="Tax / VAT ID">
                  <input className="wc-input" value={billTaxId} onChange={(e) => setBillTaxId(e.target.value)} />
                </Field>
              </div>
            </div>
          </div>

          <div>
            <div className="inv-eyebrow" style={{ marginBottom: 12 }}>
              Items
            </div>
            <div className="inv-lines">
              <div className="inv-lhead">
                <span>Description</span>
                <span className="r">Qty</span>
                <span className="r">Unit price</span>
                <span className="r">Amount</span>
                <span />
              </div>
              {lines.map((l, i) => {
                const amount = Math.round((Number.parseFloat(l.quantity || '0') || 0) * toMinor(l.unitPrice, currency));
                return (
                  <div className="inv-lrow" key={i}>
                    <input className="inv-cell" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Consulting, 3 hours" aria-label="Description" />
                    <input className="inv-cell r" inputMode="decimal" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} aria-label="Quantity" />
                    <input className="inv-cell r" inputMode="decimal" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} placeholder="0.00" aria-label="Unit price" />
                    <div className="amt">{money(amount, currency)}</div>
                    <button type="button" className="inv-rm" onClick={() => removeLine(i)} disabled={lines.length <= 1} aria-label="Remove line">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                        <path d="M4 4l8 8M12 4l-8 8" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
            <button type="button" className="inv-add" onClick={addLine}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M8 3.5v9M3.5 8h9" />
              </svg>
              Add line
            </button>

            <div className="inv-totals">
              <div className="inv-trow">
                <span>Subtotal</span>
                <span>{money(totals.subtotal, currency)}</span>
              </div>
              {totals.discountAmount > 0 ? (
                <div className="inv-trow">
                  <span>Discount</span>
                  <span>- {money(totals.discountAmount, currency)}</span>
                </div>
              ) : null}
              {totals.taxAmount > 0 ? (
                <div className="inv-trow">
                  <span>{taxLabel || 'Tax'}</span>
                  <span>{money(totals.taxAmount, currency)}</span>
                </div>
              ) : null}
              <div className="inv-trow inv-grand">
                <span>Total</span>
                <span>{money(totals.total, currency)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Settings rail */}
        <div className="inv-rail">
          <div className="inv-panel">
            <div className="inv-eyebrow">Settings</div>
            <Field label="Currency">
              <select className="wc-input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {(meta?.currencies ?? [{ code: currency, name: curLabel, symbol: '' }]).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code.toUpperCase()} — {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Payment terms">
              <select
                className="wc-input"
                value={DUE_TERMS.some((t) => String(t.days) === dueDays) ? dueDays : 'custom'}
                onChange={(e) => (e.target.value !== 'custom' ? setDueDays(e.target.value) : setDueDays('45'))}
              >
                {DUE_TERMS.map((t) => (
                  <option key={t.days} value={String(t.days)}>
                    {t.label}
                  </option>
                ))}
                <option value="custom">Custom…</option>
              </select>
            </Field>
            {!DUE_TERMS.some((t) => String(t.days) === dueDays) ? (
              <Field label="Days until due">
                <input className="wc-input wc-num" inputMode="numeric" value={dueDays} onChange={(e) => setDueDays(e.target.value)} />
              </Field>
            ) : null}
          </div>

          <div className="inv-panel">
            <div className="inv-eyebrow">Tax &amp; discount</div>
            <Field label="Tax preset">
              <select
                className="wc-input"
                value={TAX_PRESETS.find((p) => p.taxLabel === taxLabel && p.percent === taxPercent)?.label ?? 'custom'}
                onChange={(e) => {
                  const p = TAX_PRESETS.find((x) => x.label === e.target.value);
                  if (p) {
                    setTaxLabel(p.taxLabel);
                    setTaxPercent(p.percent);
                  }
                }}
              >
                {TAX_PRESETS.map((p) => (
                  <option key={p.label} value={p.label}>
                    {p.label}
                  </option>
                ))}
                <option value="custom">Custom…</option>
              </select>
            </Field>
            <div className="inv-two">
              <Field label="Tax label">
                <input className="wc-input" value={taxLabel} onChange={(e) => setTaxLabel(e.target.value)} placeholder="VAT 20%" />
              </Field>
              <Field label="Rate %">
                <input className="wc-input wc-num" inputMode="decimal" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} />
              </Field>
            </div>
            <Field label="Discount %">
              <input className="wc-input wc-num" inputMode="decimal" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} />
            </Field>
          </div>

          <div className="inv-panel">
            <div className="inv-eyebrow">References</div>
            <Field label="PO reference">
              <input className="wc-input" value={poRef} onChange={(e) => setPoRef(e.target.value)} />
            </Field>
            <Field label="Notes" hint="Printed on the invoice.">
              <textarea className="wc-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

function InvoiceDetail({
  id,
  meta,
  onBack,
  onEdit,
}: {
  id: string;
  meta: InvoiceMeta | null;
  onBack: () => void;
  onEdit: (id: string) => void;
}) {
  const [inv, setInv] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [payUrl, setPayUrl] = useState<string | null>(null);

  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('bank_transfer');
  const [payRef, setPayRef] = useState('');

  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendLink, setSendLink] = useState(false);
  const [sendNote, setSendNote] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.invoice(id);
      setInv(r.invoice);
    } catch (e) {
      setError(msg(e));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(key: string, fn: () => Promise<void>): Promise<void> {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(null);
    }
  }

  async function openPdf(): Promise<void> {
    await run('pdf', async () => {
      const blob = await api.invoicePdfBlob(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
  }

  if (error && !inv) return <Note tone="danger">{error}</Note>;
  if (!inv) return <Spinner label="Loading invoice…" />;

  const isCredit = inv.kind === 'credit_note';
  const outstanding = inv.outstanding;

  return (
    <div className="inv-wrap">
      <div className="inv-head">
        <button type="button" className="inv-back" onClick={onBack}>
          ← Back
        </button>
        <h2>
          {isCredit ? 'Credit note' : 'Invoice'} {inv.number}
        </h2>
        <StatusPill status={inv.status} />
      </div>

      {error ? <Note tone="danger">{error}</Note> : null}
      {notice ? <Note tone="ok">{notice}</Note> : null}

      <div className="inv-acts">
        {!inv.locked && inv.status === 'draft' ? (
          <>
            <button type="button" className="wc-btn wc-btn-sm" onClick={() => run('issue', async () => { await api.invoiceStatus(id, 'sent'); await load(); })} disabled={!!busy}>
              Issue
            </button>
            <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => onEdit(id)} disabled={!!busy}>
              Edit
            </button>
          </>
        ) : null}
        {meta?.pdf_available ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={openPdf} disabled={!!busy}>
            {busy === 'pdf' ? 'Opening…' : 'PDF'}
          </button>
        ) : null}
        {meta?.pdf_available ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => { setSendTo(inv.bill_to.email ?? ''); setSendOpen((v) => !v); }} disabled={!!busy}>
            Send…
          </button>
        ) : null}
        {!isCredit && outstanding > 0 && inv.status !== 'cancelled' ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => { setPayAmount(toMajor(outstanding, inv.currency)); setPayOpen((v) => !v); }} disabled={!!busy}>
            Record payment
          </button>
        ) : null}
        {!isCredit && outstanding > 0 && inv.status !== 'cancelled' ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => run('link', async () => { const r = await api.invoicePayLink(id); setPayUrl(r.url); await load(); })} disabled={!!busy}>
            {busy === 'link' ? 'Creating…' : 'Online pay link'}
          </button>
        ) : null}
        {!isCredit && inv.status !== 'draft' ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => run('cn', async () => { await api.createCreditNote(id); setNotice('Credit note created. Find it in the list.'); })} disabled={!!busy}>
            Credit note
          </button>
        ) : null}
        {!inv.locked && inv.status !== 'cancelled' ? (
          <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" onClick={() => run('cancel', async () => { await api.invoiceStatus(id, 'cancelled'); await load(); })} disabled={!!busy} style={{ marginLeft: 'auto' }}>
            Cancel
          </button>
        ) : null}
      </div>

      {payUrl ? (
        <Note tone="ok" title="Online pay link created">
          <div className="inv-link">
            <code>{payUrl}</code>
            <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => navigator.clipboard?.writeText(payUrl)}>
              Copy
            </button>
          </div>
          <div style={{ marginTop: 8 }}>The invoice is marked paid automatically when the customer pays.</div>
        </Note>
      ) : null}

      {payOpen ? (
        <Panel padded={false} title="Record a payment">
          <div style={{ padding: 16, display: 'grid', gap: 13, maxWidth: 480 }}>
            <div className="inv-two">
              <Field label={`Amount (${inv.currency.toUpperCase()})`}>
                <input className="wc-input wc-num" inputMode="decimal" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
              </Field>
              <Field label="Method">
                <select className="wc-input" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
            <Field label="Reference (optional)">
              <input className="wc-input" value={payRef} onChange={(e) => setPayRef(e.target.value)} />
            </Field>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="wc-btn wc-btn-sm"
                disabled={busy === 'pay'}
                onClick={() =>
                  run('pay', async () => {
                    await api.recordInvoicePayment(id, { amount: toMinor(payAmount, inv.currency), method: payMethod, reference: payRef.trim() || undefined });
                    setPayOpen(false);
                    setPayRef('');
                    await load();
                  })
                }
              >
                {busy === 'pay' ? 'Saving…' : 'Save payment'}
              </button>
              <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => setPayOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Panel>
      ) : null}

      {sendOpen ? (
        <Panel padded={false} title="Send by email">
          <div style={{ padding: 16, display: 'grid', gap: 13, maxWidth: 480 }}>
            <Field label="To" hint="Sent from your own mail server, with the PDF attached.">
              <input className="wc-input" type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="billing@acme.com" />
            </Field>
            {!isCredit && outstanding > 0 ? (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--wc-body)' }}>
                <input type="checkbox" checked={sendLink} onChange={(e) => setSendLink(e.target.checked)} />
                Include an online pay link
              </label>
            ) : null}
            <Field label="Note (optional)">
              <textarea className="wc-input" value={sendNote} onChange={(e) => setSendNote(e.target.value)} rows={2} />
            </Field>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="wc-btn wc-btn-sm"
                disabled={busy === 'send' || !sendTo.trim()}
                onClick={() =>
                  run('send', async () => {
                    await api.sendInvoice(id, { to: sendTo.trim(), include_pay_link: sendLink, note: sendNote.trim() || undefined });
                    setSendOpen(false);
                    setNotice('Sent.');
                    await load();
                  })
                }
              >
                {busy === 'send' ? 'Sending…' : 'Send'}
              </button>
              <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => setSendOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Panel>
      ) : null}

      <div className="inv-builder">
        <div className="inv-canvas" style={{ gap: 20 }}>
          <div>
            <div className="inv-eyebrow" style={{ marginBottom: 12 }}>
              Details
            </div>
            <div className="inv-kv">
              <div>
                <span className="k">Customer</span>
                <span className="val">{inv.bill_to.name || '—'}</span>
              </div>
              {inv.bill_to.email ? (
                <div>
                  <span className="k">Email</span>
                  <span className="val">{inv.bill_to.email}</span>
                </div>
              ) : null}
              {inv.bill_to.address ? (
                <div>
                  <span className="k">Address</span>
                  <span className="val">{inv.bill_to.address}</span>
                </div>
              ) : null}
              {inv.bill_to.tax_id ? (
                <div>
                  <span className="k">Tax ID</span>
                  <span className="val">{inv.bill_to.tax_id}</span>
                </div>
              ) : null}
              <div>
                <span className="k">Issued</span>
                <span className="val">{inv.issued_at ? dateTime(inv.issued_at) : '—'}</span>
              </div>
              <div>
                <span className="k">Due</span>
                <span className="val">{inv.due_at ? dateTime(inv.due_at) : '—'}</span>
              </div>
              {inv.po_reference ? (
                <div>
                  <span className="k">PO reference</span>
                  <span className="val">{inv.po_reference}</span>
                </div>
              ) : null}
              {inv.credits_invoice_id ? (
                <div>
                  <span className="k">Credits</span>
                  <span className="val">{inv.credits_invoice_id}</span>
                </div>
              ) : null}
              {inv.notes ? (
                <div>
                  <span className="k">Notes</span>
                  <span className="val">{inv.notes}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <div className="inv-eyebrow" style={{ marginBottom: 12 }}>
              Items
            </div>
            <div className="inv-lines">
              <div className="inv-lhead">
                <span>Description</span>
                <span className="r">Qty</span>
                <span className="r">Unit price</span>
                <span className="r">Amount</span>
                <span />
              </div>
              {inv.lines.map((l) => (
                <div className="inv-lrow" key={l.id} style={{ padding: '11px 0' }}>
                  <span style={{ color: 'var(--wc-ink)', fontSize: 13.5 }}>{l.description}</span>
                  <span className="r" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--wc-body)' }}>{l.quantity}</span>
                  <span className="r" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--wc-body)' }}>{money(l.unit_price, inv.currency)}</span>
                  <span className="amt">{money(l.amount, inv.currency)}</span>
                  <span />
                </div>
              ))}
            </div>
            <div className="inv-totals">
              <div className="inv-trow">
                <span>Subtotal</span>
                <span>{money(inv.subtotal, inv.currency)}</span>
              </div>
              {inv.discount_amount > 0 ? (
                <div className="inv-trow">
                  <span>Discount</span>
                  <span>- {money(inv.discount_amount, inv.currency)}</span>
                </div>
              ) : null}
              {inv.tax_amount > 0 ? (
                <div className="inv-trow">
                  <span>{inv.tax_label || 'Tax'}</span>
                  <span>{money(inv.tax_amount, inv.currency)}</span>
                </div>
              ) : null}
              <div className="inv-trow inv-grand">
                <span>Total</span>
                <span>{money(inv.total, inv.currency)}</span>
              </div>
              {!isCredit && inv.amount_paid > 0 ? (
                <div className="inv-trow">
                  <span>Paid</span>
                  <span>- {money(inv.amount_paid, inv.currency)}</span>
                </div>
              ) : null}
              {!isCredit && inv.amount_paid > 0 ? (
                <div className="inv-trow" style={{ fontWeight: 600, color: 'var(--wc-ink)' }}>
                  <span style={{ color: 'var(--wc-ink)' }}>Amount due</span>
                  <span>{money(outstanding, inv.currency)}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Amounts summary rail */}
        <div className="inv-rail">
          <div className="inv-panel">
            <div className="inv-eyebrow">{isCredit ? 'Credit note' : 'Amount due'}</div>
            <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--wc-ink)', fontVariantNumeric: 'tabular-nums' }}>
              {money(isCredit ? inv.total : outstanding, inv.currency)}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>
              {isCredit
                ? `Credit against ${inv.credits_invoice_id ?? 'an invoice'}.`
                : outstanding <= 0
                  ? 'Fully paid.'
                  : `of ${money(inv.total, inv.currency)} total.`}
            </div>
            {inv.payment_id ? (
              <div style={{ fontSize: 12, color: 'var(--wc-faint)' }}>Online collection enabled.</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
